import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useEffect,
} from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { useForm } from "react-hook-form";
import { Form, Button, Alert } from "react-bootstrap";
import { Nav, NavItem, NavLink, Tooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  LoginRoles,
  toastTypes,
  emptyString,
  responseType,
  errorMessages,
  loginConstants,
  IS_POS_QUESTION,
  viewPathConstants,
  surveyQuestionTabsName,
  surveyQuestionsConstants,
  saveFeedbackModalConstants,
  npsFirstFeatureStatusEnums,
  IS_NPS_FIRST,
  surveyQuestionDragStyles,
  CURSOR,
  FIRST_ARRAY_INDEX,
  EMPTY_ARRAY_LENGTH,
} from "../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  customToast,
  GenericModal,
  useNavigatingAway,
  isSuperOrBrandAdmin,
  getSurveyPreviewModal,
  isManagerOrGroupAdmin,
  handleAsyncAction,
} from "../../../../shared";
import {
  surveyQuestionActions,
  userPermissionsActions,
} from "../../redux/actions";
import { storeActions } from "../../../admin-screens/redux/actions";

import classNames from "classnames";
import AddNewQuestion from "./addNewQuestion";
import AllResponsesModal from "./shared/allResponsesModal";
import PreviewSurveyControls from "./shared/previewSurveyControls";
import SurveyQuestionListItem from "./shared/surveyQuestionListItem";
import {
  isInStoreActive,
  npsFirstFeatureStatus,
} from "../../../../shared/components/sidebarContainer/utility";
import {
  reorderQuestionsForNpsFirstSave,
  moveSentimentQuestionToStart,
  reorderDraggedQuestions,
  areQuestionOrdersEqual,
} from "./utils/surveyQuestionHelpers";

const {
  DROP_ANIMATION_DURATION,
  DRAG_OVER_PADDING,
  DRAG_OVER_GRADIENT,
} = surveyQuestionDragStyles;

const getItemStyle = ({ isDragging, isDropAnimating }, draggableStyle) => ({
  ...draggableStyle,
  background: emptyString,
  userSelect: "none",
  cursor: isDragging ? CURSOR.GRAB : CURSOR.DEFAULT,
  ...(isDropAnimating && { transitionDuration: DROP_ANIMATION_DURATION }),
});

const getListStyle = (isDraggingOver) => ({
  padding: isDraggingOver ? DRAG_OVER_PADDING : emptyString,
  marginTop: isDraggingOver ? DRAG_OVER_PADDING : emptyString,
  background: isDraggingOver ? DRAG_OVER_GRADIENT : emptyString,
});

const cloneRecipientsList = (recipients = []) =>
  recipients.map((recipient) => ({ ...recipient }));

const { SUPER_ADMIN, BRAND_ADMIN } = LoginRoles;
const { TITLE, MESSAGE, SAVE_BUTTON, DISCARD_BUTTON } =
  saveFeedbackModalConstants;

const {
  OBJECT,
  NPS_TEXT_QUESTION,
  ADD_QUESTION,
  GET_QUESTIONS,
  GET_QUESTIONS_BY_QUESTION_CATEGORY,
} = surveyQuestionsConstants;

const SurveyQuestions = (props) => {
  const {
    logedUser,
    addQuestion,
    setIsChanges,
    getAllBrands,
    setIsAddedSurvey,
    updateSurveyRecipients,
    fetchSurveyQuestions,
    getSurveyRecipients,
    emptySurveyQuestionInRedux,
    surveyQuestionsReducer: {
      isChanges,
      isAddedSurvey,
      recipientsData,
      questionsAnswerArray,
    },
    systemReducer: { allBrandsWithFeatureFlags },
  } = props;

  const {
    reset,
    register,
    resetField,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const userRole = getItemFromStorage(loginConstants.ROLE);
  const localBrandId = getItemFromStorage(loginConstants.BRAND_ID);

  const isDisabled =
    userRole === LoginRoles.GROUP || userRole === LoginRoles.MANAGER;

  const { brandId = logedUser.brandId, groupId, managerId } = useParams();

  const addRecipientsArray = useRef([]);
  const [activeTab, setActiveTab] = useState(
    surveyQuestionTabsName.onlineOrder,
  );
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [swapModal, setSwapModal] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [previewDropdownOpen, setPreviewDropdownOpen] = useState(false);
  const [isSavePreview, setIsSavePreview] = useState(false);
  const [deletedQuestions, setDeletedQuestions] = useState([]);
  const [modalElement, setModalElement] = useState(emptyString);
  const [npsAlertTooltipOpen, setNpsAlertTooltipOpen] = useState(false);
  const [questionType, setQuestionType] = useState(emptyString);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [searchString, setSearchString] = useState(emptyString);
  const [questionList, setQuestionList] = useState([...questionsAnswerArray]);

  const [deletedSecondarySurveyQuestions, setDeletedSecondarySurveyQuestions] =
    useState([]);
  const [isQuestionOrderEqual, setIsQuestionOrderEqual] = useState(false);

  const [
    showDialogLeavingPage,
    confirmNavigation,
    cancelNavigation,
    setShowDialogPrompt,
  ] = useNavigatingAway(isChanges || !isQuestionOrderEqual);

  const npsFirstFeatureStatusValue = useMemo(() => {
    return npsFirstFeatureStatus(
      allBrandsWithFeatureFlags,
      isSuperOrBrandAdmin() ? brandId : logedUser.brandId,
    );
  }, [allBrandsWithFeatureFlags, brandId, logedUser]);

  const isNpsFirstFullyActivated = useMemo(
    () => npsFirstFeatureStatusValue === npsFirstFeatureStatusEnums.FULL,
    [npsFirstFeatureStatusValue],
  );
  const isNpsFirstPartialActivated = useMemo(
    () => npsFirstFeatureStatusValue === npsFirstFeatureStatusEnums.PARTIAL,
    [npsFirstFeatureStatusValue],
  );

  //Toggle tooltip
  const toggle = () => setTooltipOpen((prev) => !prev);

  const getSavedRecipientsList = () =>
    cloneRecipientsList(
      recipientsData && recipientsData.length > EMPTY_ARRAY_LENGTH
        ? recipientsData
        : [],
    );

  const resetRecipientsListFromSavedData = () => {
    const savedRecipients = getSavedRecipientsList();
    addRecipientsArray.current = savedRecipients;
    setList(savedRecipients);
  };

  // Modal toggle.
  const isOpenToggle = () => {
    setIsOpen((prevIsOpen) => {
      if (prevIsOpen) {
        //Closing discards the unsaved draft
        resetRecipientsListFromSavedData();
      }
      return !prevIsOpen;
    });
    setSearchString(emptyString);
  };

  //Made survey questions recipients user null
  const handleRecipientsUser = (questionArray) => {
    const isPOSQuestion = activeTab === surveyQuestionTabsName.inStore;

    return questionArray.map((ques) => ({
      ...ques,
      surveyQuestionRecipients: ques.surveyQuestionRecipients?.map((r) => ({
        userId: r.userId,
        positiveResponseId: r.positiveResponseId,
        nagetiveResponseId: r.nagetiveResponseId,
        passiveResponseId: r.passiveResponseId,
        user: null,
        brand: r.brand,
        isPOSQuestion,
      })),
    }));
  };

  //Function for saving survey
  const handleSurveySubmit = (updatedArray, archivedSurveyQuestion) => {
    setIsChanges(false);
    const originalQuestions = updatedArray || questionList;

    //When NPS First is fully activated, adjust question orders
    const updateQuestionsArray = isNpsFirstFullyActivated
      ? reorderQuestionsForNpsFirstSave(originalQuestions)
      : originalQuestions.map((q) => ({ ...q }));

    const body = {
      surveyQuestions: handleRecipientsUser(updateQuestionsArray),
      deletedSurveyQuestions: deletedQuestions,
      archivedSurveyQuestion: archivedSurveyQuestion || null,
      deletedSecondarySurveyQuestions: deletedSecondarySurveyQuestions,
    };

    if (isEmptyOrNullInputField(body.surveyQuestions)) {
      setIsSavePreview(false);
      customToast(
        errorMessages.SURVEY_CREATION_VALIDATION_ERROR,
        toastTypes.INFO,
      );
      return;
    }

    if (
      brandId === localBrandId ||
      isSuperOrBrandAdmin() ||
      Number(managerId) === logedUser.id ||
      Number(groupId) === logedUser.groupId
    ) {
      addQuestion(
        body,
        isSuperOrBrandAdmin() ? brandId : logedUser.brandId,
        [],
      );
      setDeletedQuestions([]);
      setDeletedSecondarySurveyQuestions([]);
    }
  };

  const isEmptyOrNullInputField = (surveyQuestions) =>
    surveyQuestions.some(
      (item) =>
        item.questionText === null || item.questionText?.trim() === emptyString,
    );

  //To open the modal
  const openModal = () => {
    setShowModal(true);
  };

  //To close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  //Setting modal component
  const setComponent = (component) => {
    setModalElement(component);
    openModal();
  };

  //Handle adding new question button
  const handlePlusButton = () => {
    setShowAddQuestion(true);
  };

  //Handle question remove button
  const handleRemoveQuestion = (questionOrder, fieldName, id) => {
    resetField(fieldName);
    //If question id exist then add id to deleted questions id and also remove on the basis of id otherwise remove on the basis of question order
    if (id) {
      setDeletedQuestions((prev) => prev.concat(id));
      setQuestionList((prevList) => {
        const questionArray = prevList.filter((obj) => obj.id !== id);
        return handleRecipientsUser(questionArray);
      });
    } else {
      setQuestionList((prevList) => {
        const questionArray = prevList.filter(
          (obj) => obj.questionOrder !== questionOrder,
        );
        return handleRecipientsUser(questionArray);
      });
    }
  };

  //Handle preview survey
  const handlePreviewSurvey = (e, isNPSFirst = false) => {
    e.stopPropagation();
    const isPOSQuestion = activeTab === surveyQuestionTabsName.inStore; // Determine POS question status

    const urlParams = new URLSearchParams();

    urlParams.append(IS_POS_QUESTION, isPOSQuestion);

    if (isNPSFirst) {
      urlParams.append(IS_NPS_FIRST, isNPSFirst);
    }

    const previewUrl = `${
      viewPathConstants.PREVIEW_SURVEY
    }?${urlParams.toString()}`;

    if (isQuestionOrderEqual) {
      window.open(previewUrl, "_blank");
    } else {
      setComponent(
        getSurveyPreviewModal({
          onClose: closeModal,
          Title: TITLE,
          message: MESSAGE,
          ModalSize: "md",
          onConfirm: saveAndRedirect,
          onDiscard: () => discardList(isNPSFirst),
        }),
      );
    }
  };

  //Save data and redirect to preview survey
  const saveAndRedirect = () => {
    setIsSavePreview(true);
  };

  //Discard the unsaved changes and continue with the blocked navigation
  const discardChangesAndNavigate = () => {
    setIsChanges(false);
    confirmNavigation();
  };

  const discardList = (isNPSFirst = false) => {
    reset();
    emptySurveyQuestionInRedux();
    setQuestionList([...questionsAnswerArray]);

    const isPOSQuestion = activeTab === surveyQuestionTabsName.inStore;
    const urlParams = new URLSearchParams();
    urlParams.append(IS_POS_QUESTION, isPOSQuestion);

    if (isNPSFirst) {
      urlParams.append(IS_NPS_FIRST, isNPSFirst);
    }

    const previewUrl = `${
      viewPathConstants.PREVIEW_SURVEY
    }?${urlParams.toString()}`;

    window.open(previewUrl, "_blank");
    setShowModal(false);
    getSurveyQuestions(isSuperOrBrandAdmin() ? brandId : logedUser.brandId);
    setIsSavePreview(false);
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Persist the current recipients list
  const saveRecipients = () => {
    const payload = list.map(
      ({ email, roleId, name, brandId, ...rest }) => rest,
    );

    return updateSurveyRecipients(
      payload,
      isSuperOrBrandAdmin() ? brandId : logedUser.brandId,
    );
  };

  //Function to handle all responses email array
  //The modal stays open until the update succeeds, so a failure keeps the draft editable
  const allResponsesHandleSubmit = () => {
    handleAsyncAction(saveRecipients(), {
      onSuccess: () => {
        isOpenToggle();
        setSwapModal(false);
      },
    });
  };

  const responseHandle = (value, i) => {
    const updatedList = list.map((recipient, index) => {
      if (index !== i) {
        return recipient;
      }

      const updatedRecipient = { ...recipient };

      switch (value) {
        case responseType.POSITIVE:
          updatedRecipient.positiveResponseId =
            updatedRecipient.positiveResponseId ? null : value;
          break;
        case responseType.NEGATIVE:
          updatedRecipient.nagetiveResponseId =
            updatedRecipient.nagetiveResponseId ? null : value;
          break;
        case responseType.PASSIVE:
          updatedRecipient.passiveResponseId =
            updatedRecipient.passiveResponseId ? null : value;
          break;
        default:
          /* istanbul ignore next */
          break;
      }

      return updatedRecipient;
    });

    setList(updatedList);
  };

  const setRecipientsData = () => {
    resetRecipientsListFromSavedData();
    setIsOpen(true);
  };

  //Handle receive all response add recipients search field
  const handleSearchRecipients = (value) => {
    setSearchString(value);
    let listOfRecipients = addRecipientsArray.current;
    if (value !== emptyString) {
      const searchedList = listOfRecipients.filter((val) =>
        (val?.email || val?.user?.email || "")
          .toLowerCase()
          .includes(value.toLowerCase()),
      );
      setList(searchedList);
    } else {
      setList(listOfRecipients);
    }
  };

  const isDraggingDisabled = (index) => {
    const question = questionList[index];

    // Disable dragging for sentiment question and NPS text question
    return (
      question?.questionText === NPS_TEXT_QUESTION ||
      question?.isSentimentQuestion
    );
  };

  const onDragEnd = (result) => {
    //Exit if dropped outside the list or trying to drop onto a disabled position (NPS/sentiment question)
    if (!result.destination || isDraggingDisabled(result.destination.index)) {
      return;
    }

    //Reorder the questionList and set the updated list
    setQuestionList((prevList) =>
      reorderDraggedQuestions(
        prevList,
        result.source.index,
        result.destination.index,
      ),
    );
  };

  const AddQuestionButton = () => {
    return (
      <>
        {showAddQuestion && (
          <div>
            <AddNewQuestion
              key="add-new-question"
              activeTab={activeTab}
              questionType={questionType}
              questionList={questionList}
              setIsChanges={setIsChanges}
              setQuestionType={setQuestionType}
              setQuestionList={setQuestionList}
              setShowAddQuestion={setShowAddQuestion}
              handleRecipientsUser={handleRecipientsUser}
              isNpsFirstFullyActivated={isNpsFirstFullyActivated}
            />
          </div>
        )}
        {!showAddQuestion &&
          (userRole === SUPER_ADMIN || userRole === BRAND_ADMIN) && (
            <div className="text-center pt-3">
              <Button
                className="rounded-pill green-background-color green-border"
                onClick={handlePlusButton}
                data-testid="add-new-question"
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          )}
      </>
    );
  };

  //Function to handle display add button component before a question
  const handleAddButtonDisplayBefore = (q, index) => {
    // Check if the current question is the last question with NPS_TEXT_QUESTION
    const isLastNpsTextQuestion = q?.questionText === NPS_TEXT_QUESTION;

    if (isNpsFirstFullyActivated) {
      // When NPS First is fully activated, show add button only before the last question (NPS_TEXT_QUESTION)
      if (isLastNpsTextQuestion && index === questionList.length - 1) {
        return <AddQuestionButton />;
      }
    } else {
      // When NPS First is not fully activated, show add button before last two questions
      if (index === questionList.length - 2) {
        return <AddQuestionButton />;
      }
    }

    return null;
  };

  //Get survey against brandId when the page renders
  useEffect(() => {
    emptySurveyQuestionInRedux();
    getAllBrands(true);
    if (
      brandId === localBrandId ||
      userRole === SUPER_ADMIN ||
      managerId === `${logedUser.id}` ||
      groupId === `${logedUser.groupId}`
    ) {
      reset();
    }
    getSurveyRecipients(brandId || logedUser.brandId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSurveyQuestions = (brandId) => {
    // Determine whether the active tab is onlineOrder or inStore
    const dataToSend = {
      brandId: brandId,
      isPOSQuestion: activeTab === surveyQuestionTabsName.inStore,
    };

    // Pass the data to the fetchSurveyQuestions API call
    fetchSurveyQuestions(dataToSend);
  };

  const toggleTab = (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    //Iterate through the list and compare the question order for each object
    let calculatedIsQuestionOrderEqual;

    if (isNpsFirstFullyActivated) {
      // When NPS First is fully activated, the sentiment question is moved to the start for display
      // We need to compare the current questionList with the expected reordered questionsAnswerArray
      const expectedOrderedQuestions =
        moveSentimentQuestionToStart(questionsAnswerArray);

      calculatedIsQuestionOrderEqual = areQuestionOrdersEqual(
        questionList,
        expectedOrderedQuestions,
      );
    } else {
      // Normal comparison: check if questions are in the same order by index
      calculatedIsQuestionOrderEqual = areQuestionOrdersEqual(
        questionList,
        questionsAnswerArray,
      );
    }

    //Update state based on the calculation
    setIsQuestionOrderEqual(calculatedIsQuestionOrderEqual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsAnswerArray, questionList, isNpsFirstFullyActivated]);

  //Get survey against brandId when the page renders
  useEffect(() => {
    if (typeof questionsAnswerArray === OBJECT) {
      const reorderedQuestions = isNpsFirstFullyActivated
        ? moveSentimentQuestionToStart(questionsAnswerArray)
        : [...questionsAnswerArray];

      setQuestionList(reorderedQuestions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsAnswerArray, isNpsFirstFullyActivated]);

  //Update question number after removing or adding new question
  useEffect(() => {
    setQuestionList((prevList) => {
      const questionArray = prevList.map((question, index) => {
        const updatedQuestion = { ...question };
        //If we add new question then we are reseting the questionOrder, so after added question we will make all after that isEdit true.
        if (
          questionsAnswerArray.length !== prevList.length &&
          (prevList[prevList.length - 1] || prevList[prevList.length - 2])
        ) {
          updatedQuestion.isEdit = !updatedQuestion.isNew;
        }
        updatedQuestion.questionOrder = index + 1;
        return updatedQuestion;
      });
      return handleRecipientsUser(questionArray);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionList.length]);

  useEffect(() => {
    if (isSavePreview) {
      handleSurveySubmit();
    }
    // eslint-disable-next-line
  }, [isSavePreview]);

  //Get updated survey questions after saving survey
  useEffect(() => {
    if (isAddedSurvey) {
      getSurveyQuestions(isSuperOrBrandAdmin() ? brandId : logedUser.brandId);
      setIsAddedSurvey(false);
    }
    if (isSavePreview && isAddedSurvey) {
      window.open(viewPathConstants.PREVIEW_SURVEY, "_blank");
      setIsSavePreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddedSurvey]);

  useEffect(() => {
    getSurveyQuestions(brandId || logedUser.brandId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // If `inStore` is not active and currently selected, switch to `onlineOrder`
    if (
      activeTab === surveyQuestionTabsName.inStore &&
      !isInStoreActive(
        allBrandsWithFeatureFlags,
        isSuperOrBrandAdmin() ? brandId : logedUser.brandId,
      )
    ) {
      setActiveTab(surveyQuestionTabsName.onlineOrder);
    }
  }, [activeTab, allBrandsWithFeatureFlags, brandId, logedUser]);

  return (
    <>
      {spinner(GET_QUESTIONS) || spinner(ADD_QUESTION) ? (
        <Spinner />
      ) : (
        <>
          {/*Modal here */}
          {showModal && modalElement}
          <div className="container-fluid ">
            <div className="container-fluid pt-3 pb-3 shadow rounded-4 bg-white">
              <div className="tabs tabs--bordered-bottom position-relative">
                <Nav tabs className="mb-2">
                  {/* Online Order Tab */}
                  <NavItem className="cursor-pointer nav-pill-item mb-0">
                    <NavLink
                      className={classNames({
                        active:
                          activeTab === surveyQuestionTabsName.onlineOrder,
                      })}
                      onClick={() =>
                        toggleTab(surveyQuestionTabsName.onlineOrder)
                      }
                    >
                      {surveyQuestionTabsName.onlineOrder}
                    </NavLink>
                  </NavItem>

                  {/* In-Store Tab (conditionally rendered) */}
                  {isInStoreActive(
                    allBrandsWithFeatureFlags,
                    isSuperOrBrandAdmin() ? brandId : logedUser.brandId,
                  ) && (
                    <NavItem className="cursor-pointer nav-pill-item">
                      <NavLink
                        className={classNames({
                          active: activeTab === surveyQuestionTabsName.inStore,
                        })}
                        onClick={() =>
                          toggleTab(surveyQuestionTabsName.inStore)
                        }
                      >
                        {surveyQuestionTabsName.inStore}
                      </NavLink>
                    </NavItem>
                  )}
                  {isNpsFirstPartialActivated && (
                    <div className="ms-auto my-auto nps-first-alert">
                      <Alert
                        variant="info"
                        className="p-1 d-flex align-items-center px-2 m-0"
                      >
                        <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
                        <span className="d-none d-md-inline">
                          NPS First feature is partially activated. NPS First
                          Preview Survey can be view from Preview Survey
                          dropdown.
                        </span>
                        <span
                          className="d-inline d-md-none nps-alert-mobile"
                          id="npsAlertMobile"
                          onMouseEnter={() => setNpsAlertTooltipOpen(true)}
                          onMouseLeave={() => setNpsAlertTooltipOpen(false)}
                        >
                          NPS First...
                        </span>
                        <Tooltip
                          placement="bottom"
                          isOpen={npsAlertTooltipOpen}
                          target="npsAlertMobile"
                        >
                          NPS First feature is partially activated. NPS First
                          Preview Survey can be view from Preview Survey
                          dropdown.
                        </Tooltip>
                      </Alert>
                    </div>
                  )}
                </Nav>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6 col-md-8 d-flex align-items-center justify-content-sm-start justify-content-center">
                  <Button
                    className="pe-2 mb-0 green-background-color green-border"
                    onClick={setRecipientsData}
                    disabled={
                      questionList.length === FIRST_ARRAY_INDEX + 1 ||
                      spinner(GET_QUESTIONS_BY_QUESTION_CATEGORY)
                    }
                  >
                    Notifications:
                  </Button>
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    id="all-responses-tooltip"
                    className="info-icon pb-0 ps-1 pt-1 align-self-start"
                  />
                  <Tooltip
                    flip={true}
                    autohide={true}
                    toggle={toggle}
                    placement="right"
                    isOpen={tooltipOpen}
                    popperClassName="disable-link"
                    target="all-responses-tooltip"
                  >
                    Designate users to be notified for all negative or all
                    positive responses. Notifications will be sent on a per
                    survey basis.
                  </Tooltip>
                </div>
                <div className="col-12 col-sm-6 col-md-4 d-flex justify-content-end align-self-start justify-content-sm-end justify-content-center pt-sm-auto pt-1">
                  <PreviewSurveyControls
                    isNpsFirstPartialActivated={isNpsFirstPartialActivated}
                    isNpsFirstFullyActivated={isNpsFirstFullyActivated}
                    previewDropdownOpen={previewDropdownOpen}
                    setPreviewDropdownOpen={setPreviewDropdownOpen}
                    questionListLength={questionList.length}
                    onPreviewSurvey={handlePreviewSurvey}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="container-fluid pb-3">
            <Form onSubmit={handleSubmit(() => handleSurveySubmit(null, null))}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                  {(droppableProvided, droppableSnapshot) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      style={getListStyle(droppableSnapshot.isDraggingOver)}
                    >
                      {questionList.map((question, index) => (
                        <React.Fragment key={question.guid}>
                          {handleAddButtonDisplayBefore(question, index)}
                          <Draggable
                            index={index}
                            key={question.guid}
                            draggableId={`${question.guid}`}
                            isDragDisabled={isDraggingDisabled(index)}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...getItemStyle(
                                    snapshot,
                                    provided.draggableProps.style,
                                  ),
                                }}
                              >
                                <span
                                  key={question.questionId || question.guid}
                                >
                                  <SurveyQuestionListItem
                                    question={question}
                                    index={index}
                                    dragHandleProps={provided.dragHandleProps}
                                    errors={errors}
                                    register={register}
                                    resetField={resetField}
                                    localRole={isDisabled}
                                    questionList={questionList}
                                    setIsChanges={setIsChanges}
                                    setQuestionList={setQuestionList}
                                    handleSurveySubmit={handleSurveySubmit}
                                    handleRecipientsUser={handleRecipientsUser}
                                    handleRemoveQuestion={handleRemoveQuestion}
                                    deletedSecondarySurveyQuestions={
                                      deletedSecondarySurveyQuestions
                                    }
                                    setDeletedSecondarySurveyQuestions={
                                      setDeletedSecondarySurveyQuestions
                                    }
                                    questionsAnswerArray={questionsAnswerArray}
                                  />
                                </span>
                              </div>
                            )}
                          </Draggable>
                        </React.Fragment>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>{" "}
              <div className="text-center pt-3">
                {!isManagerOrGroupAdmin() && (
                  <Button
                    className="green-background-color green-border"
                    type="submit"
                  >
                    {SAVE_BUTTON}
                  </Button>
                )}
              </div>
            </Form>
          </div>
          <AllResponsesModal
            list={list}
            isOpen={isOpen}
            setList={setList}
            swapModal={swapModal}
            setSwapModal={setSwapModal}
            isOpenToggle={isOpenToggle}
            searchString={searchString}
            responseType={responseType}
            responseHandle={responseHandle}
            getSurveyRecipients={getSurveyRecipients}
            handleSearchRecipients={handleSearchRecipients}
            allResponsesHandleSubmit={allResponsesHandleSubmit}
          />
          <GenericModal
            title={TITLE}
            isBlockNavigation={true}
            onClose={discardChangesAndNavigate}
            isOpen={showDialogLeavingPage}
            onHide={() => setShowDialogPrompt(false)}
            onSubmit={() => {
              handleSurveySubmit(null, null);
              cancelNavigation();
            }}
            configuration={{
              size: "md",
              animate: true,
              submitBtnText: SAVE_BUTTON,
              cancelBtnText: DISCARD_BUTTON,
              submitBtnVariant: "dark",
              cancelBtnVariant: "outline-dark",
            }}
          >
            <div>{MESSAGE}</div>
          </GenericModal>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  systemReducer: state.system,
  logedUser: state.authentication.user,
  surveyQuestionsReducer: state.surveyQuestionsReducer,
  questionsAnswerArray: state.surveyQuestionsReducer.questionsAnswerArray,
});

const mapDispatchToProps = (dispatch) => ({
  addQuestion: (surveyQuestionList, brandId, questionArray) =>
    dispatch(
      surveyQuestionActions.addQuestion(
        surveyQuestionList,
        brandId,
        questionArray,
      ),
    ),
  setIsAddedSurvey: (flag) =>
    dispatch(surveyQuestionActions.setIsAddedSurvey(flag)),
  fetchSurveyQuestions: (brandId) =>
    dispatch(surveyQuestionActions.getSurveyQuestions(brandId)),
  getUserPermissionByBrandId: (brandId) =>
    dispatch(userPermissionsActions.getUserPermissionByBrandId(brandId)),
  getUserPermissionByGroupId: (groupId) =>
    dispatch(userPermissionsActions.getUserPermissionByGroupId(groupId)),
  getUserPermissionByManagerId: (managerId) =>
    dispatch(userPermissionsActions.getUserPermissionByManagerId(managerId)),
  setIsChanges: (flag) => dispatch(surveyQuestionActions.setIsChanges(flag)),
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
  emptySurveyQuestionInRedux: () =>
    dispatch(surveyQuestionActions.emptySurveyQuestionInRedux()),
  updateSurveyRecipients: (recipients, brandId) =>
    dispatch(surveyQuestionActions.updateSurveyRecipients(recipients, brandId)),
  getSurveyRecipients: (brandId) =>
    dispatch(surveyQuestionActions.getSurveyRecipients(brandId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(memo(SurveyQuestions));
